import { Routes } from '@angular/router';

import { authGuard, noAuthGuard } from '@core/guards';
import { rolesGuard } from '@core/guards/roles/roles-guard';
import {
  CALL_CENTER_ROLES,
  INVENTORY_MODULE_ROLES,
  LOCATION_MODULE_ROLES,
  PAYGO_MODULE_ROLES,
} from '@core/constants/module-roles';
import { LOCATION_READ_ROLES } from '@core/constants/location-roles';
export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('@core/components/layouts/admin-layout/admin-layout').then((m) => m.AdminLayout),
    canActivate: [authGuard],
    canMatch: [rolesGuard],
    children: [
      {
        // Page d'accueil, accessible à tout utilisateur authentifié : volontairement SANS
        // `data.roles`, car `rolesGuard` redirige vers `/` quand un rôle manque — un rôle
        // ici créerait une boucle de redirection.
        path: '',
        pathMatch: 'full',
        loadChildren: () => import('@features/home/home.routes').then((m) => m.routes),
      },
      {
        path: 'call-center',
        canMatch: [rolesGuard],
        data: { roles: CALL_CENTER_ROLES },
        loadChildren: () =>
          import('@features/call-management/call-management.routes').then((m) => m.routes),
      },
      {
        /**
         * Module Emplacement — **un seul préfixe** pour les trois familles d'écrans que la
         * barre latérale présente ensemble : le référentiel (liste + fiche), la carte, et
         * l'outillage Atlas.
         *
         * Auparavant servi par TROIS préfixes : `/locations`, `/location/map` et `/atlas`.
         * Les deux premiers ne différaient que d'une lettre pour deux modules différents,
         * et cet écart avait déjà demandé un avertissement en commentaire ici même.
         *
         * L'assemblage vit dans ce fichier, et pas dans `locations.routes.ts` : y charger
         * les routes de `location-map` et `atlas-admin` ferait dépendre une feature de deux
         * autres. Ici, chaque feature s'ignore et c'est l'application qui les compose.
         *
         * ⚠️ **La racine du module ne porte AUCUN écran** : elle redirige. C'est la forme
         * des trois autres modules (`/paygo/contracts`, `/inventory/warehouses`,
         * `/call-center/call-management`), et ce n'est pas cosmétique.
         *
         * Tant que la liste vivait sur `/locations`, son chemin préfixait celui de tous ses
         * voisins : `routerLinkActive` comparant par PRÉFIXE, l'entrée « Liste » du menu
         * restait surlignée sur la carte et sur les écrans Atlas. Et `:uuid`, exposé au
         * niveau du module, pouvait avaler `map` et `atlas` — l'URL continuait de
         * « marcher », mais c'est la fiche qui s'ouvrait, en cherchant un emplacement dont
         * l'uuid vaudrait « map ». Sous `list`, ce segment ne voit plus que ses frères :
         * l'ordre des écrans du module cesse d'être critique.
         */
        path: 'locations',
        canMatch: [rolesGuard],
        data: { roles: LOCATION_MODULE_ROLES },
        children: [
          {
            // La carte a son propre rôle, distinct de la lecture du référentiel.
            path: 'map',
            canMatch: [rolesGuard],
            data: { roles: ['ROLE_LOCATIONS_SHOW_MODULE'] },
            loadChildren: () =>
              import('@features/location-map/location-map.routes').then((m) => m.routes),
          },
          {
            // Outillage Atlas : configurations, générations, potentiel EaaS. Le rôle de
            // garde est porté par ATLAS_ADMIN_ROLES dans `atlas-admin.routes.ts` —
            // volontairement pas dupliqué ici (cf. son commentaire : rôle à confirmer).
            path: 'atlas',
            loadChildren: () =>
              import('@features/atlas-admin/atlas-admin.routes').then((m) => m.routes),
          },
          {
            // Le référentiel : liste sur `list`, fiche sur `list/:uuid`. Gardé par les rôles
            // de LECTURE, qui portent le cloisonnement des données côté backend
            // (ALL / LOCATION / OWN).
            path: 'list',
            canMatch: [rolesGuard],
            data: { roles: LOCATION_READ_ROLES },
            loadChildren: () =>
              import('@features/locations/locations.routes').then((m) => m.routes),
          },
          // L'écran d'accueil du module, et le SEUL : arriver par le menu ou par l'URL
          // `/locations` mène désormais au même endroit. Auparavant le menu ouvrait la
          // carte (`isDefault`) tandis que l'URL ouvrait la liste.
          { path: '', pathMatch: 'full', redirectTo: 'list' },
        ],
      },
      // Anciens chemins. Trois lignes qui évitent de casser les liens déjà partagés et les
      // signets — `pathMatch: 'prefix'` par défaut sur `atlas`, pour emporter la suite du
      // chemin (`/atlas/generations` → `/locations/atlas/generations`).
      { path: 'location/map', redirectTo: 'locations/map' },
      { path: 'location', pathMatch: 'full', redirectTo: 'locations/map' },
      { path: 'atlas', redirectTo: 'locations/atlas' },
      {
        path: 'paygo',
        canMatch: [rolesGuard],
        data: { roles: PAYGO_MODULE_ROLES },
        loadChildren: () => import('@features/paygo/paygo.routes').then((m) => m.routes),
      },
      {
        path: 'inventory',
        canMatch: [rolesGuard],
        data: { roles: INVENTORY_MODULE_ROLES },
        loadChildren: () => import('@features/inventory/inventory.routes').then((m) => m.routes),
      },
    ],
  },
  {
    path: 'login',
    loadComponent: () => import('@features/auth/pages/login/login').then((m) => m.Login),
    canActivate: [noAuthGuard],
  },
];
